import React, { useMemo, useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
} from "react-router-dom";
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import GlobalStyles from "@mui/material/GlobalStyles";
import { alpha } from "@mui/material/styles";

import LandingPage from "./components/LandingPage";
import ProductPage from "./components/ProductPage";
import PricingPage from "./components/PricingPage";
import TermsOfUsePage from "./components/TermsOfUsePage";
import AboutUs from "./components/AboutUs";
import ContactUs from "./components/ContactUs";
import FAQPage from "./components/FAQPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import BlazeAIPage from "./components/BlazeAIPage";
import DocsLayout from "./components/docs/DocsLayout";
import NonLoggedInTopNavPage from "./components/NonLoggedInTopNavPage";
import Footer from "./components/Footer";

const ScrollToTop = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return null;
};

const MarketingLayout = ({ mode, onToggleTheme }) => {
  const location = useLocation();

  return (
    <div className="app-layout">
      <NonLoggedInTopNavPage
        user={null}
        mode={mode}
        onToggleTheme={onToggleTheme}
      />
      <div className="main-content">
        <Outlet key={location.pathname} />
      </div>
      <Footer />
    </div>
  );
};

const App = () => {
  const [mode, setMode] = useState(() => {
    try {
      const saved =
        localStorage.getItem("ib-theme") || localStorage.getItem("mui-mode");
      return saved === "light" ? "light" : "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("ib-theme", mode);
      localStorage.setItem("mui-mode", mode);
    } catch {
      // ignore
    }
  }, [mode]);

  const theme = useMemo(() => {
    const isDark = mode === "dark";
    const tokens = isDark
      ? {
          ink0: "#0E1B30",
          ink1: "#152236",
          ink3: "#2A3649",
          ink6: "#7E8BA0",
          ink8: "#E6EAEF",
          ink9: "#F5F5F2",
          teal: "#3DD9D0",
          tealDim: "#2A9A94",
        }
      : {
          ink0: "#FAFAF7",
          ink1: "#FFFFFF",
          ink3: "#E5E7EA",
          ink6: "#5B6472",
          ink8: "#141820",
          ink9: "#0B0D10",
          teal: "#2A9A94",
          tealDim: "#1F7873",
        };

    return createTheme({
      palette: {
        mode,
        primary: {
          main: tokens.teal,
          light: isDark ? "#3DD9D0" : "#3DD9D0",
          dark: tokens.tealDim,
          contrastText: isDark ? "#0B0D10" : "#FAFAF7",
        },
        secondary: {
          main: tokens.ink8,
          light: isDark ? "#E6EAEF" : "#2E3540",
          dark: isDark ? "#F5F5F2" : "#0B0D10",
          contrastText: tokens.ink0,
        },
        text: {
          primary: tokens.ink8,
          secondary: tokens.ink6,
        },
        background: {
          default: tokens.ink0,
          paper: tokens.ink1,
        },
        divider: tokens.ink3,
      },
      shape: { borderRadius: 12 },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: "none",
              transition:
                "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
              border: `1px solid ${tokens.ink3}`,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: "12px",
              border: `1px solid ${tokens.ink3}`,
              boxShadow: isDark
                ? `0 6px 18px ${alpha("#000000", 0.40)}, 0 1px 3px ${alpha("#000000", 0.30)}`
                : `0 6px 18px ${alpha("#0B0D10", 0.06)}, 0 1px 3px ${alpha("#0B0D10", 0.04)}`,
            },
          },
        },
      },
    });
  }, [mode]);

  const toggleColorMode = () =>
    setMode((prev) => (prev === "light" ? "dark" : "light"));

  useEffect(() => {
    try {
      // Keep both attributes in lockstep. The head script stamps
      // data-theme on <html> before first paint (kills the flicker);
      // React must also update it on every toggle, otherwise the stale
      // <html data-theme="dark"> keeps winning the CSS cascade even after
      // body[data-mode] flips to light.
      if (document.documentElement) {
        document.documentElement.setAttribute("data-theme", mode);
      }
      if (document.body) {
        document.body.setAttribute("data-mode", mode);
      }
    } catch {
      // ignore
    }
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={(theme) => ({
          ".app-layout": {
            background: "var(--ink-0)",
            minHeight: "100dvh",
            display: "flex",
            flexDirection: "column",
          },
          ".main-content": {
            backgroundColor: "transparent",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            paddingTop: 0,
            paddingBottom: 0,
          },
          "html, body, #root": {
            height: "100%",
            background: "var(--ink-0)",
            overscrollBehaviorY: "none",
          },
          body: {
            overflowX: "hidden",
            WebkitTextSizeAdjust: "100%",
            background: "var(--ink-0)",
            color: "var(--ink-8)",
            overscrollBehaviorY: "none",
          },
          "a:not(.MuiButton-root), a:not(.MuiButton-root):visited": {
            fontFamily: theme.typography.fontFamily,
            fontSize: "0.85rem",
            textDecoration: "none",
            color: theme.palette.primary.main,
          },
          "a:not(.MuiButton-root):hover": {
            textDecoration: "underline",
            color: theme.palette.primary.dark,
          },
        })}
      />
      <Router>
        <ScrollToTop />
        <Routes>
          <Route
            element={
              <MarketingLayout
                mode={mode}
                onToggleTheme={toggleColorMode}
              />
            }
          >
            <Route path="/" element={<LandingPage />} />
            <Route path="/product" element={<ProductPage />} />
            <Route path="/blaze-ai" element={<BlazeAIPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faqs" element={<FAQPage />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            {/*
              Short aliases. These render the same components rather than
              redirecting, so a crawler that does not execute JS still gets
              full legal copy at /privacy and /terms. Each page pins its
              canonical URL to the long form.
            */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfUsePage />} />
            <Route path="/docs" element={<DocsLayout />} />
            <Route path="/docs/*" element={<DocsLayout />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
